// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect, Suspense } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Skeleton from '@mui/material/Skeleton';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/ExitToApp';
import PublicIcon from '@mui/icons-material/Public';
import SecurityIcon from '@mui/icons-material/Security';
import BugReportIcon from '@mui/icons-material/BugReport';
import ComputerIcon from '@mui/icons-material/Computer';
import StorageIcon from '@mui/icons-material/Storage';
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate';
import CodeIcon from '@mui/icons-material/Code';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import NetworkPingIcon from '@mui/icons-material/NetworkPing';
import CableIcon from '@mui/icons-material/Cable';
import BackupIcon from '@mui/icons-material/Backup';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Badge from '@mui/material/Badge';
import KinLogo from './KinLogo';
import { useAuth } from '../context/AuthContext';
import { getConfigValue, isFleetChatEnabled } from '../config';
import { useAlerts } from '../context/AlertsContext';
import { useTheme } from '../context/ThemeContext';
import SecretThemeConfig from './SecretThemeConfig';
import AlertsBanner from './AlertsBanner';

const drawerWidth = 260;

const layoutPageFallback = (
  <Box sx={{ mt: 8, px: 3 }}>
    <Skeleton
      variant="rounded"
      width="30%"
      height={28}
      sx={{ mb: 3, borderRadius: '6px' }}
      animation="wave"
    />
    <Skeleton
      variant="rounded"
      width="100%"
      height={160}
      sx={{ mb: 2, borderRadius: '8px' }}
      animation="wave"
    />
    <Skeleton
      variant="rounded"
      width="100%"
      height={160}
      sx={{ borderRadius: '8px' }}
      animation="wave"
    />
  </Box>
);

const Layout = () => {
  const { currentUser, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();
  const { alertCount, alerts: activeAlerts, bannerVisible, toggleBanner } = useAlerts();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Secret theme config
  const [themeConfigOpen, setThemeConfigOpen] = useState(false);
  const [versionClickCount, setVersionClickCount] = useState(0);
  const versionClickTimeoutRef = useState(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Close the temporary (phone) drawer after any route change — covers sidebar
  // links, footer links, alert deep-links, and avatar-menu navigations.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const drawer = (
    <Box sx={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      {/* Static left-edge accent */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '2px',
          zIndex: 1,
          pointerEvents: 'none',
          background: isDarkMode ? alpha(muiTheme.palette.primary.main, 0.22) : 'transparent',
        }}
      />

      {/* Light mode - full size partner logo */}
      {!isDarkMode && (
        <Box
          sx={{
            width: '260px',
            height: '140px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#ffffff', // White background for better logo visibility
            borderBottom: '2px solid #211E71', // Blue accent border
          }}
        >
          <img
            src="/test-partner.jpg"
            alt="Partner Logo"
            style={{
              maxWidth: '240px', // Increased from 80% to ~92% for better visibility
              maxHeight: '120px', // Increased from 80% to ~86% for better visibility
              width: 'auto',
              height: 'auto',
              objectFit: 'contain', // Preserve aspect ratio
              display: 'block',
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          height: isDarkMode ? '100%' : 'calc(100% - 140px)', // Adjust height in light mode
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          // Dark: solid ink to match main chrome. Light: partner green/blue stripes.
          background: isDarkMode
            ? '#0b0c18'
            : `linear-gradient(118deg, 
              transparent 0%, 
              transparent 12%,
              rgba(139, 195, 74, 0.15) 12%, 
              rgba(139, 195, 74, 0.15) 19%, 
              rgba(30, 70, 112, 0.10) 19%,
              rgba(30, 70, 112, 0.10) 22%,
              transparent 22%,
              transparent 40%, 
              rgba(139, 195, 74, 0.08) 40%, 
              rgba(139, 195, 74, 0.08) 41%, 
              rgba(30, 70, 112, 0.06) 41%,
              rgba(30, 70, 112, 0.06) 42%,
              transparent 42%,
              transparent 65%,
              rgba(139, 195, 74, 0.18) 65%, 
              rgba(139, 195, 74, 0.18) 73%, 
              rgba(30, 70, 112, 0.12) 73%,
              rgba(30, 70, 112, 0.12) 76%,
              transparent 76%,
              transparent 89%,
              rgba(139, 195, 74, 0.07) 89%,
              rgba(139, 195, 74, 0.07) 91%,
              rgba(30, 70, 112, 0.05) 91%,
              rgba(30, 70, 112, 0.05) 92%,
              transparent 92%),
             linear-gradient(180deg, #6ab04c 0%, #5a9e3e 100%)`,
          backgroundSize: !isDarkMode ? '180px 180px, 100% 100%' : 'auto',
        }}
      >
        {isDarkMode && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
              height: 128,
              width: drawerWidth,
              px: 1.5,
              py: 1.5,
              boxSizing: 'border-box',
            }}
          >
            <KinLogo
              variant="reverse"
              height="100%"
              width="100%"
              maxWidth="100%"
              style={{ maxHeight: '100%' }}
            />
          </Box>
        )}

        <Box sx={{ p: 2, position: 'relative' }}>
          <Chip
            icon={<SecurityIcon fontSize="small" />}
            label={`${currentUser?.role?.toUpperCase()} ACCESS`}
            color={isAdmin() ? 'primary' : 'secondary'}
            variant="outlined"
            size="small"
            sx={{
              borderRadius: 1,
              '& .MuiChip-label': {
                fontWeight: 600,
                fontSize: '0.7rem',
                color: isDarkMode ? 'inherit' : '#ffffff',
              },
              '& .MuiChip-icon': {
                color: isDarkMode ? 'inherit' : '#ffffff',
              },
              borderColor: isDarkMode ? 'inherit' : 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                borderColor: isAdmin()
                  ? muiTheme.palette.primary.main
                  : muiTheme.palette.secondary.main,
              },
            }}
          />
        </Box>

        <Divider
          sx={{
            borderColor: 'rgba(255, 255, 255, 0.08)',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              left: '10%',
              right: '10%',
              bottom: 0,
              height: '1px',
              background: `linear-gradient(90deg,
            transparent 0%,
            ${alpha(muiTheme.palette.primary.main, 0.3)} 20%,
            ${alpha(muiTheme.palette.primary.main, 0.6)} 50%,
            ${alpha(muiTheme.palette.primary.main, 0.3)} 80%,
            transparent 100%)`,
              boxShadow: `0 0 8px ${alpha(muiTheme.palette.primary.main, 0.4)}`,
            },
          }}
        />

        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
          <List component="nav">
            <ListItem
              button
              onClick={() => navigate('/')}
              selected={isActive('/')}
              sx={{
                mb: 1,
                borderRadius: 2,
                color: isDarkMode ? 'inherit' : '#ffffff',
                '&.Mui-selected': {
                  backgroundColor: alpha(muiTheme.palette.primary.main, 0.15),
                  '&:hover': {
                    backgroundColor: alpha(muiTheme.palette.primary.main, 0.25),
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '20%',
                    bottom: '20%',
                    width: 4,
                    borderRadius: '0 4px 4px 0',
                    backgroundColor: muiTheme.palette.primary.main,
                    color: muiTheme.palette.primary.main,
                  },
                },
                '&:hover': {
                  backgroundColor: isDarkMode
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(255, 255, 255, 0.15)',
                },
              }}
            >
              <ListItemIcon>
                <PublicIcon
                  sx={{
                    color: isActive('/')
                      ? muiTheme.palette.primary.main
                      : isDarkMode
                        ? 'inherit'
                        : '#ffffff',
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Geographical Regions"
                primaryTypographyProps={{
                  fontWeight: isActive('/') ? 600 : 400,
                  fontSize: '0.95rem',
                  color: isDarkMode ? 'inherit' : '#ffffff',
                }}
              />
            </ListItem>

            {isAdmin() && (
              <>
                <ListItem
                  button
                  onClick={() => navigate('/remote-connections')}
                  selected={isActive('/remote-connections')}
                  sx={{
                    mb: 1,
                    borderRadius: 2,
                    color: isDarkMode ? 'inherit' : '#ffffff',
                    '&.Mui-selected': {
                      backgroundColor: alpha(muiTheme.palette.primary.main, 0.15),
                      '&:hover': {
                        backgroundColor: alpha(muiTheme.palette.primary.main, 0.25),
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '20%',
                        bottom: '20%',
                        width: 4,
                        borderRadius: '0 4px 4px 0',
                        backgroundColor: muiTheme.palette.primary.main,
                      },
                    },
                    '&:hover': {
                      backgroundColor: isDarkMode
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'rgba(255, 255, 255, 0.15)',
                    },
                  }}
                >
                  <ListItemIcon>
                    <ComputerIcon
                      sx={{
                        color: isActive('/remote-connections')
                          ? muiTheme.palette.primary.main
                          : isDarkMode
                            ? 'inherit'
                            : '#ffffff',
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Connection Manager"
                    primaryTypographyProps={{
                      fontWeight: isActive('/remote-connections') ? 600 : 400,
                      fontSize: '0.95rem',
                      color: isDarkMode ? 'inherit' : '#ffffff',
                    }}
                  />
                </ListItem>

                <ListItem
                  button
                  onClick={() => navigate('/registry')}
                  selected={isActive('/registry')}
                  sx={{
                    mb: 1,
                    borderRadius: 2,
                    color: isDarkMode ? 'inherit' : '#ffffff',
                    '&.Mui-selected': {
                      backgroundColor: alpha(muiTheme.palette.primary.main, 0.15),
                      '&:hover': {
                        backgroundColor: alpha(muiTheme.palette.primary.main, 0.25),
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '20%',
                        bottom: '20%',
                        width: 4,
                        borderRadius: '0 4px 4px 0',
                        backgroundColor: muiTheme.palette.primary.main,
                      },
                    },
                    '&:hover': {
                      backgroundColor: isDarkMode
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'rgba(255, 255, 255, 0.15)',
                    },
                  }}
                >
                  <ListItemIcon>
                    <StorageIcon
                      sx={{
                        color: isActive('/registry')
                          ? muiTheme.palette.primary.main
                          : isDarkMode
                            ? 'inherit'
                            : '#ffffff',
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Container Registry"
                    primaryTypographyProps={{
                      fontWeight: isActive('/registry') ? 600 : 400,
                      fontSize: '0.95rem',
                      color: isDarkMode ? 'inherit' : '#ffffff',
                    }}
                  />
                </ListItem>
              </>
            )}

            <ListItem
              button
              onClick={() => navigate('/debug')}
              selected={isActive('/debug')}
              sx={{
                mb: 1,
                borderRadius: 2,
                color: isDarkMode ? 'inherit' : '#ffffff',
                '&.Mui-selected': {
                  backgroundColor: alpha(muiTheme.palette.primary.main, 0.15),
                  '&:hover': {
                    backgroundColor: alpha(muiTheme.palette.primary.main, 0.25),
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '20%',
                    bottom: '20%',
                    width: 4,
                    borderRadius: '0 4px 4px 0',
                    backgroundColor: muiTheme.palette.primary.main,
                    color: muiTheme.palette.primary.main,
                  },
                },
                '&:hover': {
                  backgroundColor: isDarkMode
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(255, 255, 255, 0.15)',
                },
              }}
            >
              <ListItemIcon>
                <BugReportIcon
                  sx={{
                    color: isActive('/debug')
                      ? muiTheme.palette.primary.main
                      : isDarkMode
                        ? 'inherit'
                        : '#ffffff',
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Debug"
                primaryTypographyProps={{
                  fontWeight: isActive('/debug') ? 600 : 400,
                  fontSize: '0.95rem',
                  color: isDarkMode ? 'inherit' : '#ffffff',
                }}
              />
            </ListItem>

            {isFleetChatEnabled() && (
              <ListItem
                button
                onClick={() => navigate('/fleet-chat')}
                selected={isActive('/fleet-chat')}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  color: isDarkMode ? 'inherit' : '#ffffff',
                  '&.Mui-selected': {
                    backgroundColor: alpha(muiTheme.palette.primary.main, 0.15),
                    '&:hover': {
                      backgroundColor: alpha(muiTheme.palette.primary.main, 0.25),
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '20%',
                      bottom: '20%',
                      width: 4,
                      borderRadius: '0 4px 4px 0',
                      backgroundColor: muiTheme.palette.primary.main,
                      color: muiTheme.palette.primary.main,
                    },
                  },
                  '&:hover': {
                    backgroundColor: isDarkMode
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(255, 255, 255, 0.15)',
                  },
                }}
              >
                <ListItemIcon>
                  <SmartToyIcon
                    sx={{
                      color: isActive('/fleet-chat')
                        ? muiTheme.palette.primary.main
                        : isDarkMode
                          ? 'inherit'
                          : '#ffffff',
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Fleet Chat"
                  primaryTypographyProps={{
                    fontWeight: isActive('/fleet-chat') ? 600 : 400,
                    fontSize: '0.95rem',
                    color: isDarkMode ? 'inherit' : '#ffffff',
                  }}
                />
              </ListItem>
            )}

            <ListItem
              button
              onClick={() => navigate('/network-metrics')}
              selected={isActive('/network-metrics')}
              sx={{
                mb: 1,
                borderRadius: 2,
                color: isDarkMode ? 'inherit' : '#ffffff',
                '&.Mui-selected': {
                  backgroundColor: alpha(muiTheme.palette.primary.main, 0.15),
                  '&:hover': {
                    backgroundColor: alpha(muiTheme.palette.primary.main, 0.25),
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '20%',
                    bottom: '20%',
                    width: 4,
                    borderRadius: '0 4px 4px 0',
                    backgroundColor: muiTheme.palette.primary.main,
                    color: muiTheme.palette.primary.main,
                  },
                },
                '&:hover': {
                  backgroundColor: isDarkMode
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(255, 255, 255, 0.15)',
                },
              }}
            >
              <ListItemIcon>
                <ShowChartIcon
                  sx={{
                    color: isActive('/network-metrics')
                      ? muiTheme.palette.primary.main
                      : isDarkMode
                        ? 'inherit'
                        : '#ffffff',
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Network Metrics"
                primaryTypographyProps={{
                  fontWeight: isActive('/network-metrics') ? 600 : 400,
                  fontSize: '0.95rem',
                  color: isDarkMode ? 'inherit' : '#ffffff',
                }}
              />
            </ListItem>

            <ListItem
              button
              onClick={() => navigate('/topology')}
              selected={isActive('/topology')}
              sx={{
                mb: 1,
                borderRadius: 2,
                color: isDarkMode ? 'inherit' : '#ffffff',
                '&.Mui-selected': {
                  backgroundColor: alpha(muiTheme.palette.primary.main, 0.15),
                  '&:hover': {
                    backgroundColor: alpha(muiTheme.palette.primary.main, 0.25),
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '20%',
                    bottom: '20%',
                    width: 4,
                    borderRadius: '0 4px 4px 0',
                    backgroundColor: muiTheme.palette.primary.main,
                    color: muiTheme.palette.primary.main,
                  },
                },
                '&:hover': {
                  backgroundColor: isDarkMode
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(255, 255, 255, 0.15)',
                },
              }}
            >
              <ListItemIcon>
                <AccountTreeIcon
                  sx={{
                    color: isActive('/topology')
                      ? muiTheme.palette.primary.main
                      : isDarkMode
                        ? 'inherit'
                        : '#ffffff',
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Network Topology"
                primaryTypographyProps={{
                  fontWeight: isActive('/topology') ? 600 : 400,
                  fontSize: '0.95rem',
                  color: isDarkMode ? 'inherit' : '#ffffff',
                }}
              />
            </ListItem>

            <ListItem
              button
              onClick={() => navigate('/edge-latency')}
              selected={isActive('/edge-latency')}
              sx={{
                mb: 1,
                borderRadius: 2,
                color: isDarkMode ? 'inherit' : '#ffffff',
                '&.Mui-selected': {
                  backgroundColor: alpha(muiTheme.palette.primary.main, 0.15),
                  '&:hover': {
                    backgroundColor: alpha(muiTheme.palette.primary.main, 0.25),
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '20%',
                    bottom: '20%',
                    width: 4,
                    borderRadius: '0 4px 4px 0',
                    backgroundColor: muiTheme.palette.primary.main,
                    color: muiTheme.palette.primary.main,
                  },
                },
                '&:hover': {
                  backgroundColor: isDarkMode
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(255, 255, 255, 0.15)',
                },
              }}
            >
              <ListItemIcon>
                <NetworkPingIcon
                  sx={{
                    color: isActive('/edge-latency')
                      ? muiTheme.palette.primary.main
                      : isDarkMode
                        ? 'inherit'
                        : '#ffffff',
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Edge Latency"
                primaryTypographyProps={{
                  fontWeight: isActive('/edge-latency') ? 600 : 400,
                  fontSize: '0.95rem',
                  color: isDarkMode ? 'inherit' : '#ffffff',
                }}
              />
            </ListItem>

            {isAdmin() && (
              <ListItem
                button
                onClick={() => navigate('/mass-firmware-update')}
                selected={isActive('/mass-firmware-update')}
                sx={{
                  mb: 1,
                  borderRadius: 2,
                  color: isDarkMode ? 'inherit' : '#ffffff',
                  '&.Mui-selected': {
                    backgroundColor: alpha(muiTheme.palette.primary.main, 0.15),
                    '&:hover': {
                      backgroundColor: alpha(muiTheme.palette.primary.main, 0.25),
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '20%',
                      bottom: '20%',
                      width: 4,
                      borderRadius: '0 4px 4px 0',
                      backgroundColor: muiTheme.palette.primary.main,
                    },
                  },
                  '&:hover': {
                    backgroundColor: isDarkMode
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(255, 255, 255, 0.15)',
                  },
                }}
              >
                <ListItemIcon>
                  <SystemUpdateIcon
                    sx={{
                      color: isActive('/mass-firmware-update')
                        ? muiTheme.palette.primary.main
                        : isDarkMode
                          ? 'inherit'
                          : '#ffffff',
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Stargate SW Update"
                  primaryTypographyProps={{
                    fontWeight: isActive('/mass-firmware-update') ? 600 : 400,
                    fontSize: '0.95rem',
                    color: isDarkMode ? 'inherit' : '#ffffff',
                  }}
                />
              </ListItem>
            )}
          </List>
        </Box>

        <Box
          sx={{
            p: 2,
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '10%',
              right: '10%',
              top: 0,
              height: '1px',
              background: `linear-gradient(90deg,
            transparent 0%,
            ${alpha(muiTheme.palette.primary.main, 0.2)} 20%,
            ${alpha(muiTheme.palette.primary.main, 0.5)} 50%,
            ${alpha(muiTheme.palette.primary.main, 0.2)} 80%,
            transparent 100%)`,
              boxShadow: `0 0 6px ${alpha(muiTheme.palette.primary.main, 0.3)}`,
            },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 0.25,
              color: isDarkMode ? 'text.secondary' : '#ffffff',
            }}
          >
            © {new Date().getFullYear()} Isolated Networks
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 0.75,
              color: isDarkMode ? 'text.secondary' : '#ffffff',
              cursor: 'default',
              userSelect: 'none',
            }}
            onClick={() => {
              // Secret trigger: click version 3 times to open theme config
              const newCount = versionClickCount + 1;
              setVersionClickCount(newCount);

              if (newCount >= 3) {
                setThemeConfigOpen(true);
                setVersionClickCount(0);
              }

              // Reset count after 2 seconds
              if (versionClickTimeoutRef.current) {
                clearTimeout(versionClickTimeoutRef.current);
              }
              versionClickTimeoutRef.current = setTimeout(() => {
                setVersionClickCount(0);
              }, 2000);
            }}
          >
            Version {process.env.REACT_APP_VERSION || '1.6.323'}
          </Typography>
          {(window.RUNTIME_CONFIG?.host || window.location.hostname) && (
            <Typography
              variant="caption"
              title="Host WireGuard IP — the address Starfleet is reachable on. Click to copy."
              onClick={() => {
                try {
                  navigator.clipboard.writeText(
                    window.RUNTIME_CONFIG?.host || window.location.hostname
                  );
                } catch (e) {
                  /* clipboard unavailable */
                }
              }}
              sx={{
                display: 'block',
                mb: 0.5,
                color: isDarkMode ? 'text.disabled' : alpha('#ffffff', 0.7),
                fontSize: '0.65rem',
                fontFamily: 'monospace',
                cursor: 'pointer',
                '&:hover': { color: isDarkMode ? 'primary.main' : '#ffffff' },
              }}
            >
              📡 WireGuard IP: {window.RUNTIME_CONFIG?.host || window.location.hostname}
            </Typography>
          )}
          <Typography
            variant="caption"
            component="a"
            onClick={() => navigate('/licenses')}
            sx={{
              display: 'block',
              mb: 0.25,
              color: isDarkMode ? 'text.disabled' : alpha('#ffffff', 0.7),
              textDecoration: 'none',
              cursor: 'pointer',
              fontSize: '0.65rem',
              '&:hover': {
                color: isDarkMode ? 'primary.main' : '#ffffff',
                textDecoration: 'underline',
              },
            }}
          >
            📜 Open Source Software
          </Typography>
          <Typography
            variant="caption"
            component="a"
            onClick={() => navigate('/security')}
            sx={{
              display: 'block',
              mb: 0.25,
              color: isDarkMode ? 'text.disabled' : alpha('#ffffff', 0.7),
              textDecoration: 'none',
              cursor: 'pointer',
              fontSize: '0.65rem',
              '&:hover': {
                color: isDarkMode ? 'primary.main' : '#ffffff',
                textDecoration: 'underline',
              },
            }}
          >
            🛡️ Security Report
          </Typography>
          <Typography
            variant="caption"
            component="a"
            onClick={() => navigate('/host-metrics')}
            sx={{
              display: 'block',
              color: isDarkMode ? 'text.disabled' : alpha('#ffffff', 0.7),
              textDecoration: 'none',
              cursor: 'pointer',
              fontSize: '0.65rem',
              '&:hover': {
                color: isDarkMode ? 'primary.main' : '#ffffff',
                textDecoration: 'underline',
              },
            }}
          >
            📊 Host Resources
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', position: 'relative' }}>
      {/* Accent separator line - different styles for light/dark mode */}
      {/* Hide when sidebar is folded (narrow view) */}
      <Box
        sx={{
          position: 'fixed',
          left: { xs: 0, sm: drawerWidth },
          top: 0,
          bottom: 0,
          width: !isDarkMode ? '5px' : '3px',
          backgroundColor: !isDarkMode
            ? '#211E71'
            : 'rgba(255, 255, 255, 0.08)',
          zIndex: muiTheme.zIndex.drawer + 1,
          boxShadow: !isDarkMode ? '2px 0 8px rgba(30, 70, 112, 0.15)' : 'none',
          pointerEvents: 'none',
          display: { xs: 'none', sm: 'block' },
        }}
      />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          overflow: 'hidden',
          backdropFilter: 'blur(10px)',
          // Use theme-based background with parallel green and blue stripes for light mode
          background: isDarkMode
            ? 'rgba(11, 12, 24, 0.85)'
            : `linear-gradient(118deg, 
                transparent 0%,
                transparent 10%, 
                rgba(139, 195, 74, 0.14) 10%, 
                rgba(139, 195, 74, 0.14) 16%, 
                rgba(30, 70, 112, 0.09) 16%,
                rgba(30, 70, 112, 0.09) 19%,
                transparent 19%, 
                transparent 38%,
                rgba(139, 195, 74, 0.06) 38%, 
                rgba(139, 195, 74, 0.06) 39%, 
                rgba(30, 70, 112, 0.05) 39%,
                rgba(30, 70, 112, 0.05) 40%,
                transparent 40%,
                transparent 62%, 
                rgba(139, 195, 74, 0.16) 62%, 
                rgba(139, 195, 74, 0.16) 69%, 
                rgba(30, 70, 112, 0.11) 69%,
                rgba(30, 70, 112, 0.11) 72%,
                transparent 72%,
                transparent 87%,
                rgba(139, 195, 74, 0.08) 87%, 
                rgba(139, 195, 74, 0.08) 89%, 
                rgba(30, 70, 112, 0.06) 89%,
                rgba(30, 70, 112, 0.06) 90%,
                transparent 90%),
               linear-gradient(90deg, #5a9e3e 0%, #6ab04c 100%)`,
          backgroundSize: !isDarkMode ? '200px 80px, 100% 100%' : 'auto',
          borderTop: !isDarkMode ? '2px solid #211E71' : 'none',
          borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #689f38',
          color: isDarkMode ? '#ffffff' : '#ffffff',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: { xs: 1, sm: 2 }, display: { sm: 'none' }, flexShrink: 0 }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h5"
            component="div"
            sx={{
              fontWeight: 700,
              color: '#ffffff',
              fontSize: { xs: '1.15rem', sm: '1.445rem' },
              ml: { xs: 0, sm: 1 },
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Starfleet
          </Typography>

          {getConfigValue('portalUrl') ? (
            <Button
              component="a"
              href={String(getConfigValue('portalUrl')).replace(/\/$/, '')}
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
              size="small"
              sx={{
                ml: 2,
                textTransform: 'none',
                fontWeight: 600,
                opacity: 0.9,
                whiteSpace: 'nowrap',
                minWidth: 'auto',
              }}
            >
              ← Portal
            </Button>
          ) : null}

          <Box sx={{ flexGrow: 1, minWidth: 8 }} />

          {/* Alert bell icon with badge — toggles banner visibility */}
          {alertCount > 0 && (
            <Tooltip
              title={
                bannerVisible
                  ? 'Hide alerts'
                  : `Show ${alertCount} alert${alertCount !== 1 ? 's' : ''}`
              }
            >
              <IconButton
                color="inherit"
                onClick={toggleBanner}
                sx={{
                  mr: 1,
                  animation:
                    activeAlerts.some((a) => a.severity === 'critical') && !bannerVisible
                      ? 'alertPulse 2s ease-in-out infinite'
                      : 'none',
                  '@keyframes alertPulse': {
                    '0%': { color: 'inherit' },
                    '50%': { color: '#ff6b6b' },
                    '100%': { color: 'inherit' },
                  },
                }}
              >
                <Badge
                  badgeContent={alertCount}
                  color="error"
                  max={9}
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.65rem',
                      height: 16,
                      minWidth: 16,
                    },
                  }}
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Help Videos">
            <IconButton color="inherit" aria-label="help videos" onClick={() => navigate('/help')}>
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Account Settings">
            <IconButton
              edge="end"
              color="inherit"
              aria-label="account settings"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: isAdmin()
                    ? isDarkMode
                      ? 'primary.main'
                      : '#211E71' // Dark blue from Add Region button
                    : isDarkMode
                      ? 'secondary.main'
                      : '#211E71',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {currentUser?.username?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem disabled>
              <Typography
                variant="body2"
                sx={{ color: isDarkMode ? 'text.primary' : 'text.secondary' }}
              >
                Signed in as <strong>{currentUser?.username}</strong>
              </Typography>
            </MenuItem>
            <Divider />
            {isAdmin() && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  navigate('/remote-admin');
                }}
              >
                <ListItemIcon>
                  <ComputerIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="body2">Remote Admin</Typography>
              </MenuItem>
            )}
            {isAdmin() && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  navigate('/api-docs');
                }}
              >
                <ListItemIcon>
                  <CodeIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="body2">API Documentation</Typography>
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate('/mqtt-connect');
              }}
            >
              <ListItemIcon>
                <CableIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="body2">MQTT Connect</Typography>
            </MenuItem>
            {isAdmin() && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  navigate('/backup');
                }}
              >
                <ListItemIcon>
                  <BackupIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="body2">Backup &amp; Restore</Typography>
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="body2">Logout</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: isDarkMode
                ? '#0b0c18'
                : `linear-gradient(118deg, 
                    transparent 0%, 
                    transparent 12%,
                    rgba(139, 195, 74, 0.15) 12%, 
                    rgba(139, 195, 74, 0.15) 19%, 
                    rgba(30, 70, 112, 0.10) 19%,
                    rgba(30, 70, 112, 0.10) 22%,
                    transparent 22%,
                    transparent 40%, 
                    rgba(139, 195, 74, 0.08) 40%, 
                    rgba(139, 195, 74, 0.08) 41%, 
                    rgba(30, 70, 112, 0.06) 41%,
                    rgba(30, 70, 112, 0.06) 42%,
                    transparent 42%,
                    transparent 65%,
                    rgba(139, 195, 74, 0.18) 65%, 
                    rgba(139, 195, 74, 0.18) 73%, 
                    rgba(30, 70, 112, 0.12) 73%,
                    rgba(30, 70, 112, 0.12) 76%,
                    transparent 76%,
                    transparent 89%,
                    rgba(139, 195, 74, 0.07) 89%,
                    rgba(139, 195, 74, 0.07) 91%,
                    rgba(30, 70, 112, 0.05) 91%,
                    rgba(30, 70, 112, 0.05) 92%,
                    transparent 92%),
                   linear-gradient(180deg, #6ab04c 0%, #5a9e3e 100%)`,
              backgroundSize: !isDarkMode ? '180px 180px, 100% 100%' : 'auto',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: isDarkMode ? '#0b0c18' : 'none',
              borderRight: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
              padding: 0,
              margin: 0,
              overflow: 'visible',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // Avoid `p` + `pt` shorthand fights: set axes explicitly.
          px: { xs: 1.5, sm: 3 },
          pb: { xs: 1.5, sm: 3 },
          pt: { xs: 8, sm: 9 },
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          minWidth: 0,
          height: '100vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          // Use theme-based background - dark blue for dark mode, light for light mode
          backgroundColor: isDarkMode ? 'rgba(11, 12, 24, 0.95)' : 'transparent',
          position: 'relative',
          boxSizing: 'border-box',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            zIndex: 1,
            minWidth: 0,
            width: '100%',
            // Fleet Chat fills the leftover pane; other routes keep min-height:
            // auto so tall pages still grow and `main` scrolls.
            ...(location.pathname.startsWith('/fleet-chat')
              ? { minHeight: 0, overflow: 'hidden' }
              : {}),
            // Drop Container gutters on phones — Layout already pads. sm+ keeps
            // MUI's default gutters so desktop spacing is unchanged.
            px: { xs: 0, sm: 3 },
            // Ensure container doesn't override the background
            backgroundColor: 'transparent',
          }}
        >
          {location.pathname.startsWith('/fleet-chat') ? (
            <>
              <Box sx={{ flexShrink: 0 }}>
                <AlertsBanner />
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Suspense fallback={layoutPageFallback}>
                  <Outlet />
                </Suspense>
              </Box>
            </>
          ) : (
            <>
              <AlertsBanner />
              <Suspense fallback={layoutPageFallback}>
                <Outlet />
              </Suspense>
            </>
          )}
        </Container>
      </Box>

      {/* Secret Theme Configuration Dialog */}
      <SecretThemeConfig open={themeConfigOpen} onClose={() => setThemeConfigOpen(false)} />
    </Box>
  );
};

export default Layout;
